/**
 * ProcessingBanner — floating bottom-right panel that appears while
 * image files are being analyzed by Claude Vision.
 *
 * Module-level helpers (addPendingImages / clearPendingImages) let any
 * component update the state without prop drilling. State is persisted
 * in localStorage so it survives a page refresh.
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { IconButton } from './ui';

const STORAGE_KEY = 'compass:pending-images';
const CHANGED_EVENT = 'compass:pending-images-changed';

export function getPendingImages(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function addPendingImages(filenames: string[]): void {
  const current = getPendingImages();
  const updated = [...new Set([...current, ...filenames])];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

export function clearPendingImages(): void {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

export function ProcessingBanner() {
  const [pending, setPending] = useState<string[]>(getPendingImages);
  const location = useLocation();

  useEffect(() => {
    function refresh() {
      setPending(getPendingImages());
    }
    window.addEventListener(CHANGED_EVENT, refresh);
    window.addEventListener('storage', refresh); // cross-tab updates
    return () => {
      window.removeEventListener(CHANGED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  // Clear when user visits the Dashboards gallery
  useEffect(() => {
    if (location.pathname === '/dashboards' && pending.length > 0) {
      clearPendingImages();
    }
  }, [location.pathname, pending.length]);

  if (pending.length === 0) return null;

  const label =
    pending.length === 1
      ? `Analyzing "${pending[0]}" with Claude Vision`
      : `Analyzing ${pending.length} screenshot${pending.length > 1 ? 's' : ''} with Claude Vision`;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 2000,
        background: 'var(--bg-secondary)',
        border: '1px solid var(--accent)',
        borderRadius: 12,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        boxShadow: '0 8px 32px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)',
        maxWidth: 380,
        fontSize: 13,
      }}
    >
      <SpinnerIcon />
      <div>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
          {label}
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 3 }}>
          Building your dashboard in the background
        </div>
      </div>
      <IconButton
        size="sm"
        tone="muted"
        aria-label="Dismiss notification"
        onClick={clearPendingImages}
        style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 16, borderRadius: 4 }}
      >
        ×
      </IconButton>
    </div>
  );
}

function SpinnerIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--accent)"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden
      style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
